import AiAllowance from '../AiAllowance.jsx';
// src/components/practice/EssayFeedbackTiers.jsx
//
// L3 -- the two differentiated feedback CTAs shown on a practice essay's done
// screen (flow B: two CTAs, NOT a forced modal). GENERIC + reusable (any practice
// essay, any pack type). Props are server-known facts from the current item.
//
//   PRIMARY  "Get instant AI feedback" -> gradeEssay({packId, promptId, essayText}).
//            Included with the pack; needs PACK entitlement even for a free prompt.
//   SECONDARY "Submit for Tutor Review" -> a pre-submit quote-request panel (no
//            payment, no auto-attach): creates a tutor_review serviceRequest.
//
// Acknowledged owner-only draft snapshots precede AI requests. Graded history
// remains best-effort; a failed model/history save cannot remove the draft.
// No private text is stored in local/sessionStorage. Tutor requests never attach
// AI feedback (it must not bias the human marker).
import { saveSchoolEssayAttempt } from '../../lib/callables.js';
import { buildAiFeedback } from '../../storage/essayAttemptHelpers.js';
import React, { useState, useMemo, useEffect } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db, auth, AUTH_MODE } from '../../auth/firebase.js';
import { useAuth } from '../../auth/useAuth.js';
import { useEssayAttemptWriter } from '../../storage/useEssayAttempts.js';
import { gradeEssay } from '../../lib/callables.js';
import { IconSparkles, IconRefresh, IconUser } from '../icons.jsx';
import { failureMessage, hashEssay, buildCopyText, diagnosticReference } from './aiFeedbackMessages.js';
import MarkSheet from './MarkSheet.jsx';
import { aiWaitCopy, AI_WAIT_ESCALATE_MS } from '../aiWaitCopy.js';

const COOLDOWN_MS = 8000;
const TUTOR_FEE_FALLBACK = 'Fee will be confirmed by Danielle before work begins.';

export default function EssayFeedbackTiers({ packId, promptId, promptTitle, essayText, entitled, tutorFeeNote, schoolSave }) {
    const { currentUser } = useAuth();
    const { saveAttempt, saveDraft } = useEssayAttemptWriter();
    const [draftState, setDraftState] = useState('idle');
    const [diagnosticId, setDiagnosticId] = useState('');
    const essay = String(essayText || '');
    const hasEssay = essay.trim().length > 0;

    // ---- AI feedback state (all ephemeral) ----
    const [aiState, setAiState] = useState('idle'); // idle | loading | done | error
    const [aiResult, setAiResult] = useState(null);
    const [aiError, setAiError] = useState(null);
    const [aiInputHash, setAiInputHash] = useState(null);
    const [cooldown, setCooldown] = useState(false);
    const [copied, setCopied] = useState(false);
    // The ~20s grading call is non-streamed; `waitLong` flips true once a single wait
    // crosses the threshold to escalate the reassurance copy. It never touches the call.
    const [waitLong, setWaitLong] = useState(false);

    // Escalate the wait copy after a continuous threshold. Cleanup clears the timer
    // when loading ends (done/error via runAi's transitions) or the component unmounts.
    useEffect(() => {
        if (aiState !== 'loading') { setWaitLong(false); return; }
        const t = setTimeout(() => setWaitLong(true), AI_WAIT_ESCALATE_MS);
        return () => clearTimeout(t);
    }, [aiState]);

    const currentHash = useMemo(() => hashEssay(packId, promptId, essay), [packId, promptId, essay]);
    const hasResult = aiState === 'done' && !!aiResult;
    const essayChanged = hasResult && aiInputHash !== currentHash;

    const runAi = async () => {
        if (!entitled || aiState === 'loading' || cooldown) return;
        // Unchanged essay + an existing result -> a regenerate is a deliberate redo.
        if (hasResult && !essayChanged
            && !window.confirm('Regenerate AI feedback? This replaces the current feedback.')) return;
        setAiState('loading'); setAiError(null); setDiagnosticId('');
        const usedHash = currentHash;
        const owner = currentUser?.uid;
        try {
            setDraftState('saving');
            try { await saveDraft({ packId, promptId, promptTitle, essayText: essay }); }
            catch { setDraftState('error'); setAiError('draft_save_failed'); setAiState('error'); return; }
            if (auth?.currentUser?.uid !== owner) return;
            setDraftState('saved');
            const res = await gradeEssay({ packId, promptId, essayText: essay });
            if (auth?.currentUser?.uid !== owner) return;
            if (res && res.status === 'graded') {
                setAiResult(res); setAiInputHash(usedHash); setAiState('done');
                // ADDITIVE (Scope B): ALSO persist this attempt + its feedback to the
                // user's My Essays history. Best-effort -- the writer swallows errors,
                // so a failed write never disrupts the ephemeral feedback display.
                const savedId = await schoolSave?.current;
                if (savedId) { try { await saveSchoolEssayAttempt({ attemptId: savedId, packId, promptId, essayText: essay, aiFeedback: buildAiFeedback(res) }); } catch { setAiError('Your AI feedback is shown here, but could not be added to the school report.'); } }
                else saveAttempt({ packId, promptId, promptTitle, essayText: essay, graded: res, entitled });
            } else {
                setDiagnosticId(diagnosticReference(res?.diagnosticId));
                setAiError((res && res.reason) || 'unavailable'); setAiState('error');
            }
        } catch {
            setAiError('unavailable'); setAiState('error');
        } finally {
            setCooldown(true);
            setTimeout(() => setCooldown(false), COOLDOWN_MS);
        }
    };

    const saveOnly = async () => {
        if (draftState === 'saving' || aiState === 'loading') return;
        setDraftState('saving');
        try { await saveDraft({ packId, promptId, promptTitle, essayText: essay }); setDraftState('saved'); }
        catch { setDraftState('error'); }
    };

    const copyFeedback = async () => {
        try {
            await navigator.clipboard.writeText(buildCopyText(aiResult));
            setCopied(true); setTimeout(() => setCopied(false), 2000);
        } catch { /* clipboard blocked -- non-fatal */ }
    };

    const aiBusy = aiState === 'loading' || cooldown;
    const aiCtaLabel = aiState === 'loading' ? 'Generating feedback…'
        : hasResult ? 'Regenerate feedback' : 'Get instant AI feedback';

    return (
        <div className="mt-8 border-t border-book-accent/20 pt-6 space-y-6">
            <AiAllowance kind="grading" packId={packId} />
            {currentUser?.uid && <div className="space-y-2">
                <button type="button" onClick={saveOnly} disabled={!hasEssay || draftState === 'saving' || aiState === 'loading'}
                    className="px-4 py-2 border border-book-accent/40 text-book-accent text-sm disabled:opacity-40">
                    {draftState === 'saving' ? 'Saving draft…' : 'Save draft'}
                </button>
                <p role="status" className="text-book-muted text-xs">
                    {draftState === 'saved' ? 'Draft snapshot saved in Account → My Essays. Save again after editing.'
                        : draftState === 'error' ? failureMessage('draft_save_failed')
                            : 'Save a draft without using AI. A draft snapshot is also saved before AI marking starts.'}
                </p>
            </div>}
            {/* ============ PRIMARY: AI feedback ============ */}
            <div>
                {!entitled ? (
                    // Include-after-unlock framing -- NOT "not allowed". No gradeEssay call.
                    <div className="border border-book-accent/30 bg-book-page/40 p-5">
                        <div className="flex items-center gap-2 mb-2">
                            <IconSparkles className="w-4 h-4 text-book-accent" />
                            <div className="font-display text-book-accent text-xs font-semibold">Instant AI feedback</div>
                        </div>
                        <p className="font-serif italic text-book-muted text-sm leading-relaxed">{failureMessage('not_entitled')}</p>
                    </div>
                ) : (
                    <>
                        <button onClick={runAi} disabled={aiBusy || !hasEssay}
                            className="inline-flex items-center gap-2 px-5 py-3 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-semibold">
                            {aiState === 'loading'
                                ? <span className="inline-block w-3.5 h-3.5 border-2 border-black/40 border-t-black rounded-full animate-spin" />
                                : <IconSparkles className="w-4 h-4" />}
                            {aiCtaLabel}
                        </button>
                        <p className="font-serif italic text-book-muted text-xs mt-2">
                            Included with this pack. Gives task-specific feedback and next steps; scores depend on the applicable rubric and available evidence.
                            Up to 10 AI marks per day, shared with your own-essay marking in Account;
                            you can re-generate feedback once per essay.
                        </p>
                        {essayChanged && (
                            <p className="font-serif italic text-amber-300 text-xs mt-2">Essay changed -- feedback may no longer match.</p>
                        )}
                        {aiState === 'loading' && (
                            // Expectation-setting panel near where the feedback will render, so
                            // the ~20s non-streamed wait reads as deliberate, not stalled. The
                            // button keeps its own spinner + "Generating feedback..." label.
                            <div className="mt-4 border border-book-accent/20 bg-book-page/40 p-4 flex items-center gap-3"
                                role="status" aria-live="polite">
                                <span className="inline-block w-4 h-4 border-2 border-book-accent/40 border-t-book-accent rounded-full animate-spin flex-shrink-0" />
                                <span className="font-serif italic text-book-muted text-sm">{aiWaitCopy('grading', waitLong)}</span>
                            </div>
                        )}
                    </>
                )}

                {/* SUCCESS: the shared mark sheet (lesson-3c) -- the SAME component the
                    My Essays history renders, so the two views cannot drift. The tutor
                    nudge + Copy feedback ride in as children, above the disclaimer. */}
                {hasResult && (
                    <div className="mt-4 space-y-4">
                        <p className="font-serif italic text-book-text leading-relaxed">
                            Nice work getting a full draft down. Here's some formative feedback to push this draft further.
                        </p>
                        <MarkSheet fb={aiResult}>
                            <p className="font-serif italic text-book-muted text-sm">
                                For a more detailed human mark on this draft, you can request a Tutor Review below.
                            </p>
                            <button onClick={copyFeedback}
                                className="px-4 py-2 rounded-full border border-book-accent/50 text-book-accent font-display text-sm hover:bg-book-accent/10 transition-colors font-semibold">
                                {copied ? 'Copied' : 'Copy feedback'}
                            </button>
                        </MarkSheet>
                    </div>
                )}

                {/* FAILURE: calm, specific, essay never cleared */}
                {aiState === 'error' && (
                    <div className="mt-4 border border-amber-500/40 bg-amber-500/5 p-4">
                        <p className="font-serif text-book-text text-sm leading-relaxed">{failureMessage(aiError)}</p>
                        {diagnosticId && <p className="text-book-muted text-xs mt-2 break-all">Support reference: {diagnosticId}</p>}
                        <details className="selectable mt-3 text-book-text text-sm">
                            <summary className="cursor-pointer">Recover / copy your answer</summary>
                            <textarea aria-label="Your answer for recovery" readOnly value={essay} rows={6}
                                className="selectable mt-2 w-full bg-book-bg border border-book-accent/30 p-3 font-serif" />
                        </details>
                    </div>
                )}
            </div>

            {/* ============ SECONDARY: Tutor Review (always available, even after an AI failure) ============ */}
            <TutorReviewCta
                packId={packId} promptId={promptId} promptTitle={promptTitle}
                essay={essay} hasEssay={hasEssay} uid={currentUser?.uid} tutorFeeNote={tutorFeeNote} />
        </div>
    );
}

// Pre-submit quote-request panel. No payment, no auto-attach -- the essay is only
// sent after an explicit confirm. Writes a tutor_review serviceRequest with context.
function TutorReviewCta({ packId, promptId, promptTitle, essay, hasEssay, uid, tutorFeeNote }) {
    const [open, setOpen] = useState(false);
    const [showEssay, setShowEssay] = useState(false);
    const [state, setState] = useState('idle'); // idle | submitting | sent
    const [error, setError] = useState(null);

    const feeNote = (typeof tutorFeeNote === 'string' && tutorFeeNote.trim()) ? tutorFeeNote.trim() : TUTOR_FEE_FALLBACK;

    const submit = async () => {
        setError(null);
        if (AUTH_MODE === 'mock' || !db) { setError('Tutor Review needs the live app (emulator or firebase mode).'); return; }
        if (!uid) { setError('Please sign in to request a Tutor Review.'); return; }
        if (!hasEssay) { setError('Write an essay first.'); return; }
        setState('submitting');
        try {
            // Context rides in the free-form payload (rules allow it). The AI result
            // is deliberately NOT included -- it must not bias the human marker.
            const payload = { submissionText: essay, source: 'practice_essay', packId, promptId };
            if (promptTitle) { payload.promptTitle = promptTitle; payload.contentTitle = promptTitle; }
            await addDoc(collection(db, 'serviceRequests'), {
                type: 'tutor_review', uid, status: 'requested', payload, createdAt: serverTimestamp(),
            });
            setState('sent');
        } catch (e) {
            setError((e && e.message) || 'Could not send your request. Please try again.');
            setState('idle');
        }
    };

    if (state === 'sent') {
        return (
            <div className="border border-book-accent/30 bg-book-page/30 p-5">
                <div className="font-display text-book-accent text-xs mb-1 font-semibold">Request sent</div>
                <p className="font-serif text-book-text text-sm leading-relaxed">
                    Your Tutor Review request has been sent to Danielle. Track it in Account -&gt; Tutor Review, where you'll
                    receive a quote and payment instructions before any marking begins.
                </p>
            </div>
        );
    }

    if (!open) {
        return (
            <div>
                <button onClick={() => setOpen(true)}
                    className="inline-flex items-center gap-2 px-5 py-3 border border-book-accent/50 text-book-accent font-display text-xs hover:bg-book-accent/10 transition-colors font-semibold">
                    <IconUser className="w-4 h-4" /> Submit for Tutor Review
                </button>
                <p className="font-serif italic text-book-muted text-xs mt-2">Paid marking by Danielle. Submit a request and our team will send a quote. Marking begins after you agree to the fee and payment is received.</p>
            </div>
        );
    }

    return (
        <div className="border border-book-accent/30 bg-book-page/40 p-5 space-y-3">
            <div className="font-display text-book-accent text-xs font-semibold">Request a Tutor Review</div>
            <p className="font-serif text-book-text text-sm leading-relaxed">
                Tutor Review is premium human marking by Danielle. She'll review your request and send a quote / payment
                instruction before marking begins. No payment is taken now.
            </p>
            <p className="font-serif italic text-book-muted text-sm">{feeNote}</p>

            <div className="border border-book-accent/20 bg-book-bg/40 p-3">
                <div className="font-serif italic text-book-muted text-xs mb-1">The essay below will be sent to Danielle.</div>
                <button onClick={() => setShowEssay((v) => !v)} className="font-serif italic text-book-accent text-xs hover:underline">
                    {showEssay ? 'Hide essay' : 'Preview essay'}
                </button>
                {showEssay && (
                    <p className="mt-2 font-serif text-book-text text-sm leading-relaxed whitespace-pre-wrap border-l-2 border-book-accent/30 pl-3 max-h-48 overflow-y-auto">
                        {hasEssay ? essay : <span className="italic opacity-60">— no essay written —</span>}
                    </p>
                )}
            </div>

            {error && <p className="font-serif text-red-300 text-sm border border-red-500/40 bg-red-500/10 p-3">{error}</p>}

            <div className="flex flex-wrap gap-3 pt-1">
                <button onClick={submit} disabled={state === 'submitting' || !hasEssay}
                    className="px-5 py-2.5 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-semibold">
                    {state === 'submitting' ? 'Sending…' : 'Request Tutor Review Quote'}
                </button>
                <button onClick={() => setOpen(false)} disabled={state === 'submitting'}
                    className="px-5 py-2.5 border border-book-accent/40 text-book-muted font-display text-xs hover:text-book-accent transition-colors font-semibold">
                    Cancel
                </button>
            </div>
        </div>
    );
}
